import { useState, useEffect, useRef } from "react";
import Layout from "./../Layout";
import { useNavigate } from "react-router-dom";
import {
  MapPin,
  Heart,
  Mountain,
  Droplet,
  Eye,
  Tag,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API_BASE_URL = "https://stage.beachcabana.lk/hotelmanage/api.php";
const AUTH_HEADER = {
  headers: {
    Authorization: "Basic YWRtaW46cGFzc3dvcmQ=",
  },
};

const categoryIcons = {
  Viewpoint: Eye,
  Hiking: Mountain,
  Waterfall: Droplet,
  default: Tag,
};

export default function ThingsToDo() {
  const [thingsToDo, setThingsToDo] = useState([]);
  const [carouselIndices, setCarouselIndices] = useState({});
  const [wishlistItems, setWishlistItems] = useState({});
  const [visibleItems, setVisibleItems] = useState(9);
  const [pausedCarousels, setPausedCarousels] = useState({});
  const carouselRefs = useRef({});

  const navigate = useNavigate();

  // Fetch wishlist from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("wishlistItems")) || {};
    setWishlistItems(saved);
  }, []);

  // Save wishlist to localStorage
  useEffect(() => {
    localStorage.setItem("wishlistItems", JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  // Fetch things to do from API
  useEffect(() => {
    fetch(`${API_BASE_URL}?param=thingstodo`, AUTH_HEADER)
      .then((res) => res.json())
      .then((data) => {
        if (data.code === 200 && data.data) {
          setThingsToDo(data.data);
          const initIndices = {};
          data.data.forEach((item) => {
            initIndices[item.id] = 0;
          });
          setCarouselIndices(initIndices);
        }
      })
      .catch((error) => console.error("Error fetching things to do:", error));
  }, []);

  // Auto-slide for each carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndices((prev) => {
        const updated = { ...prev };
        thingsToDo.forEach((item) => {
          if (!pausedCarousels[item.id]) {
            const urls = JSON.parse(item.image_urls || "[]");
            if (urls.length > 1) {
              updated[item.id] = (prev[item.id] + 1) % urls.length;
            }
          }
        });
        return updated;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [thingsToDo, pausedCarousels]);

  // Infinite scroll
  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 100
      ) {
        setVisibleItems((prev) => Math.min(prev + 6, thingsToDo.length));
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [thingsToDo]);

  // Navigation to detail page
  const handleCardClick = (slug) => {
    navigate(`/thingstodo/${slug}`);
  };

  // Toggle wishlist
  const toggleWishlist = (itemId) => {
    setWishlistItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  // Carousel controls
  const nextSlide = (itemId, urls) => {
    setCarouselIndices((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] + 1) % urls.length,
    }));
  };

  const prevSlide = (itemId, urls) => {
    setCarouselIndices((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] - 1 + urls.length) % urls.length,
    }));
  };

  const goToSlide = (itemId, index) => {
    setCarouselIndices((prev) => ({
      ...prev,
      [itemId]: index,
    }));
  };

  // Touch support
  const handleTouchStart = (itemId, e) => {
    setPausedCarousels((prev) => ({ ...prev, [itemId]: true }));
    carouselRefs.current[itemId] = { startX: e.targetTouches[0].clientX };
  };

  const handleTouchMove = (itemId, e) => {
    if (carouselRefs.current[itemId]) {
      carouselRefs.current[itemId].endX = e.targetTouches[0].clientX;
    }
  };

  const handleTouchEnd = (itemId, urls) => {
    if (carouselRefs.current[itemId]?.startX && carouselRefs.current[itemId]?.endX) {
      const distance = carouselRefs.current[itemId].startX - carouselRefs.current[itemId].endX;
      const minSwipeDistance = 50;
      if (Math.abs(distance) > minSwipeDistance) {
        if (distance > 0) {
          nextSlide(itemId, urls);
        } else {
          prevSlide(itemId, urls);
        }
      }
    }
    setPausedCarousels((prev) => ({ ...prev, [itemId]: false }));
  };

  return (
    <Layout pageTitle="Things To Do" background="false">
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {thingsToDo.slice(0, visibleItems).map((item) => {
            const imageUrls = JSON.parse(item.image_urls || "[]");
            const currentImageIndex = carouselIndices[item.id] || 0;
            const Icon = categoryIcons[item.category] || categoryIcons.default;

            return (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition cursor-pointer"
                onClick={(e) => {
                  // Prevent card navigation when clicking carousel controls
                  if (e.target.closest("button")) return;
                  handleCardClick(item.slug);
                }}
              >
                <div
                  className="relative w-full h-48"
                  onMouseEnter={() =>
                    setPausedCarousels((prev) => ({ ...prev, [item.id]: true }))
                  }
                  onMouseLeave={() =>
                    setPausedCarousels((prev) => ({ ...prev, [item.id]: false }))
                  }
                  onTouchStart={(e) => handleTouchStart(item.id, e)}
                  onTouchMove={(e) => handleTouchMove(item.id, e)}
                  onTouchEnd={() => handleTouchEnd(item.id, imageUrls)}
                  role="region"
                  aria-label={`Carousel for ${item.title}`}
                >
                {/* Carousel Container */}
<div className="relative w-full h-full overflow-hidden rounded-t-lg">
  <div
    className="flex h-full w-full transition-transform duration-500 ease-in-out will-change-transform"
    style={{
      transform: `translateX(-${currentImageIndex * 100}%)`,
    }}
  >
    {imageUrls.map((src, i) => (
      <div key={i} className="w-full h-full flex-shrink-0">
        <img
          src={`images/thingstodo/${src}`}
          className="w-full h-full object-cover select-none"
          alt={`${item.title} slide ${i + 1}`} // Changed "image" to "slide"
          loading={i === currentImageIndex ? "eager" : "lazy"}
        />
      </div>
    ))}
  </div>
</div>
                  {/* Indicators */}
                  {imageUrls.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 space-x-2 z-30">
                      {imageUrls.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`h-2 w-2 rounded-full ${
                            i === currentImageIndex ? "bg-white" : "bg-white/50"
                          } hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-white`}
                          aria-label={`Go to image ${i + 1} for ${item.title}`}
                          aria-current={i === currentImageIndex ? "true" : "false"}
                          onClick={(e) => {
                            e.stopPropagation();
                            goToSlide(item.id, i);
                          }}
                        ></button>
                      ))}
                    </div>
                  )}

                  {/* Controls */}
                  {imageUrls.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="absolute left-0 top-0 flex h-full items-center justify-center px-3 focus:outline-none group"
                        onClick={(e) => {
                          e.stopPropagation();
                          prevSlide(item.id, imageUrls);
                        }}
                        aria-label={`Previous image for ${item.title}`}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/50 group-hover:bg-black/70 group-focus:ring-2 group-focus:ring-white transition-colors">
                          <ChevronLeft className="h-5 w-5 text-white" />
                        </span>
                      </button>
                      <button
                        type="button"
                        className="absolute right-0 top-0 flex h-full items-center justify-center px-3 focus:outline-none group"
                        onClick={(e) => {
                          e.stopPropagation();
                          nextSlide(item.id, imageUrls);
                        }}
                        aria-label={`Next image for ${item.title}`}
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/50 group-hover:bg-black/70 group-focus:ring-2 group-focus:ring-white transition-colors">
                          <ChevronRight className="h-5 w-5 text-white" />
                        </span>
                      </button>
                    </>
                  )}

                  {/* Wishlist Button */}
                  <button
                    type="button"
                    className="absolute top-2 right-2 p-2 rounded-full bg-white/80 hover:bg-white focus:ring-2 focus:ring-blue-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWishlist(item.id);
                    }}
                    aria-label={wishlistItems[item.id] ? "Remove from wishlist" : "Add to wishlist"}
                  >
                    <Heart
                      className={`h-5 w-5 ${
                        wishlistItems[item.id] ? "text-red-500 fill-red-500" : "text-gray-500"
                      }`}
                    />
                  </button>
                </div>
                <div className="p-4">
                  <h2 className="text-xl font-bold text-gray-800">{item.title}</h2>
                  <p className="text-gray-600 text-sm mb-2">{item.short_description}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Icon className="w-4 h-4 text-blue-500" />
                      {item.category}
                    </span>
                    {item.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-green-500" />
                        {item.location}
                      </span>
                    )}
                  </div>
                  {(item.open_time || item.close_time || item.distance_from_ella_km) && (
                    <div className="flex items-center justify-between text-sm text-gray-500 mt-2">
                      {(item.open_time || item.close_time) && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-purple-500" />
                          {item.open_time?.substring(0, 5)} - {item.close_time?.substring(0, 5)}
                        </span>
                      )}
                      {item.distance_from_ella_km && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-yellow-500" />
                          {item.distance_from_ella_km} km
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}